import { Button } from '@/components/ui/Button';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';

interface PreviusStepButtonProps {
	onClick: () => void;
	isFirstStep: boolean;
}

const PreviusStepButton = ({
	onClick,
	isFirstStep = false,
}: PreviusStepButtonProps) => {
	return (
		<Button
			variant="outline"
			data-testid={E2E_SCHEDULING.FOOTER_BACK}
			onClick={() => onClick()}
			text={isFirstStep ? 'Cancelar' : 'Voltar'}
			startIcon={
				isFirstStep ? (
					<X className="size-4 shrink-0 transition-transform group-hover:scale-95" />
				) : (
					<ArrowLeft className="size-4 shrink-0 transition-transform group-hover:-translate-x-0.5" />
				)
			}
			className="group inline-flex h-11 w-full min-w-0 items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-4 text-sm font-bold text-stone-700 shadow-sm hover:border-stone-300 hover:bg-stone-50 sm:px-5"
		/>
	);
};

interface NextStepButtonProps {
	onClick: () => void;
	disabled: boolean;
	nextStepText?: string;
}

const NextStepButton = ({
	onClick,
	disabled,
	nextStepText = 'Próxima etapa',
}: NextStepButtonProps) => {
	return (
		<Button
			variant="contained"
			data-testid={E2E_SCHEDULING.FOOTER_NEXT}
			disabled={disabled}
			onClick={() => onClick()}
			text={nextStepText}
			endIcon={
				<ArrowRight className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
			}
			className="h-11 w-max max-w-full shrink-0 justify-center rounded-xl bg-stone-900 px-4 text-sm font-black uppercase tracking-wide text-white shadow-sm hover:bg-stone-800 disabled:opacity-50 disabled:hover:bg-stone-900 sm:px-5"
		/>
	);
};

interface StepButtonContainerProps {
	onHandlePrevius?: () => void;
	onHandleNext?: () => void;
	isFirstStep?: boolean;
	disabledNextButton?: boolean;
	nextStepText?: string;
	showNextButton?: boolean;
}

const StepButtonContainer = ({
	onHandlePrevius,
	onHandleNext,
	isFirstStep = false,
	disabledNextButton = false,
	nextStepText,
	showNextButton = true,
}: StepButtonContainerProps) => {
	const hasPrev = Boolean(onHandlePrevius);
	const hasNext = Boolean(showNextButton && onHandleNext);

	return (
		<div
			className={
				hasPrev && hasNext
					? 'flex w-full items-center gap-3'
					: `flex w-full items-center gap-3 ${
							hasPrev ? 'justify-start' : 'justify-end'
					  }`
			}
		>
			{onHandlePrevius && (
				<div
					className={
						hasNext
							? 'w-full max-w-[120px] shrink-0'
							: 'w-full max-w-[120px]'
					}
				>
					<PreviusStepButton
						isFirstStep={isFirstStep}
						onClick={onHandlePrevius}
					/>
				</div>
			)}
			{showNextButton && onHandleNext && (
				<div
					className={
						hasPrev ? 'flex min-w-0 flex-1 justify-end' : undefined
					}
				>
					<NextStepButton
						onClick={onHandleNext}
						disabled={disabledNextButton}
						nextStepText={nextStepText}
					/>
				</div>
			)}
		</div>
	);
};

export { PreviusStepButton, NextStepButton, StepButtonContainer };
